import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, Plus, MessageCircle, User } from "lucide-react";
import { db } from "../firebase";
import { ref, onValue, get } from "firebase/database";
import { useAuth } from "../context/AuthContext";

export default function BottomNav() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [unreadChats, setUnreadChats] = useState(0);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  useEffect(() => {
    if (!currentUser) return;
    // Read user's chat index first (secure), then check each chat for unread
    const userChatsRef = ref(db, `userChats/${currentUser.uid}`);
    const unsub = onValue(userChatsRef, async (snap) => {
      const buyerIds = snap.exists() ? Object.keys(snap.val()) : [];
      // Also check sellerChats for chats started by buyers
      let sellerIds = [];
      try {
        const sSnap = await get(ref(db, `sellerChats/${currentUser.uid}`));
        if (sSnap.exists()) sellerIds = Object.keys(sSnap.val());
      } catch (e) {}
      const chatIds = [...new Set([...buyerIds, ...sellerIds])];
      if (chatIds.length === 0) { setUnreadChats(0); return; }
      let count = 0;
      await Promise.all(chatIds.map(async (cid) => {
        try {
          const msgsSnap = await get(ref(db, `chats/${cid}/messages`));
          if (msgsSnap.exists()) {
            const hasUnread = Object.values(msgsSnap.val()).some(
              m => m.senderId !== currentUser.uid && m.read === false
            );
            if (hasUnread) count++;
          }
        } catch (e) {}
      }));
      setUnreadChats(count);
    });
    return unsub;
  }, [currentUser]);

  const navItem = (path, icon, label) => {
    const active = isActive(path);
    return (
      <Link
        to={path}
        className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all tap-scale ${
          active
            ? "text-blue-600"
            : "text-gray-400 hover:text-gray-600"
        }`}
      >
        <div className={`p-1 rounded-lg transition-all ${active ? "bg-blue-50" : ""}`}>
          {React.cloneElement(icon, { size: 20, strokeWidth: active ? 2.5 : 1.8 })}
        </div>
        <span className={`text-xs font-medium ${active ? "text-blue-600" : "text-gray-400"}`}>
          {label}
        </span>
      </Link>
    );
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 max-w-lg mx-auto">
      <div className="bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] flex items-center justify-around px-2 py-1">
        {navItem("/", <Home />, "Home")}
        {navItem("/browse", <Search />, "Browse")}

        {/* Center Post button */}
        <Link
          to="/sell"
          className="flex flex-col items-center gap-0.5 -mt-6 tap-scale"
        >
          <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl p-3.5 shadow-lg shadow-blue-200 border-[3px] border-white">
            <Plus size={24} strokeWidth={2.5} />
          </div>
          <span className="text-xs font-semibold text-blue-600 mt-0.5">Post</span>
        </Link>

        {/* Chat with badge */}
        <Link
          to="/chat"
          className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all tap-scale ${
            isActive("/chat") ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
          }`}
        >
          <div className={`p-1 rounded-lg relative ${isActive("/chat") ? "bg-blue-50" : ""}`}>
            <MessageCircle size={20} strokeWidth={isActive("/chat") ? 2.5 : 1.8} />
            {unreadChats > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none">
                {unreadChats > 9 ? "9+" : unreadChats}
              </span>
            )}
          </div>
          <span className={`text-xs font-medium ${isActive("/chat") ? "text-blue-600" : "text-gray-400"}`}>
            Chats
          </span>
        </Link>

        {navItem("/profile", <User />, "Profile")}
      </div>
    </nav>
  );
}
